import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Mail, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { photoSrc, saveSettings, useSite } from "@/lib/site";
import { PasswordPanel } from "@/components/PasswordPanel";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Bits";
import { SiteContentEditor } from "@/components/SiteContentEditor";

const L = ({ children }) => <Label className="text-xs font-mono uppercase text-muted-foreground">{children}</Label>;

const AlertsPanel = ({ s, setS }) => {
  const { user, setUser } = useAuth();
  const [login, setLogin] = useState(user?.email || "");
  const saveAlerts = async () => {
    try { setS((await saveSettings(s)).data); toast.success("Alert email saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const saveLogin = async () => {
    try { setUser((await api.put("/admin/account", { email: login })).data); toast.success("Login email updated. Use it next time you log in."); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-6 lg:p-8 space-y-5" data-testid="alerts-panel">
      <h3 className="text-xl flex items-center gap-2"><Mail className="h-5 w-5 text-pinkb" />Email alerts & login</h3>
      <p className="text-sm text-muted-foreground">You'll get an email when an AI draft needs approving or a member asks for you personally. Members are emailed when their answer is released.</p>
      <div className="space-y-2"><L>Send my alerts to</L>
        <div className="flex gap-2">
          <Input type="email" value={s.alert_email || ""} onChange={(e) => setS({ ...s, alert_email: e.target.value })} placeholder="you@yourdomain.co.uk" data-testid="alert-email-input" className="bg-white" />
          <Button onClick={saveAlerts} data-testid="alert-email-save-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white">Save</Button>
        </div>
      </div>
      <div className="space-y-2"><L>My admin login email</L>
        <div className="flex gap-2">
          <Input type="email" value={login} onChange={(e) => setLogin(e.target.value)} data-testid="admin-login-email-input" className="bg-white" />
          <Button onClick={saveLogin} variant="outline" data-testid="admin-login-email-save-btn" className="rounded-full">Update</Button>
        </div>
      </div>
    </div>
  );
};

const FocusPanel = ({ s, setS, photos }) => {
  const save = async () => {
    try { setS((await saveSettings(s)).data); toast.success("Dashboard banner updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-6 lg:p-8 space-y-5" data-testid="focus-panel">
      <h3 className="text-xl">This month's focus (member dashboard banner)</h3>
      <div className="space-y-2"><L>Message (max 200 characters)</L>
        <Input value={s.focus_text || ""} maxLength={200} onChange={(e) => setS({ ...s, focus_text: e.target.value })} data-testid="focus-text-input" className="bg-white" />
      </div>
      <div className="space-y-2"><L>Banner photo: click to choose</L>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {photos.map((p, i) => {
            const active = (s.focus_photo_id || photos[0]?.id) === p.id;
            return (
              <button key={p.id} onClick={() => setS({ ...s, focus_photo_id: p.id })} data-testid={`focus-photo-${i}`}
                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors ${active ? "border-pinkb" : "border-transparent"}`}>
                <img src={photoSrc(p.url)} alt={p.alt} className="w-full h-full object-cover" />
                {active && <Check className="absolute top-1 right-1 h-4 w-4 text-white bg-pinkb rounded-full p-0.5" />}
              </button>
            );
          })}
        </div>
      </div>
      <Button onClick={save} data-testid="focus-save-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white">Save banner</Button>
    </div>
  );
};

const PhotoCard = ({ p, i, onChanged }) => {
  const [caption, setCaption] = useState(p.caption);
  const save = () => api.put(`/admin/photos/${p.id}`, { caption }).then(() => { toast.success("Caption saved"); onChanged(); });
  const remove = async () => {
    if (!window.confirm("Remove this photo from the site?")) return;
    await api.delete(`/admin/photos/${p.id}`);
    onChanged();
  };
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`site-photo-${i}`}>
      <img src={photoSrc(p.url)} alt={p.alt} className="w-full h-44 object-cover" />
      <div className="p-3 flex gap-2">
        <Input value={caption} onChange={(e) => setCaption(e.target.value)} onBlur={() => caption !== p.caption && save()} data-testid={`site-photo-caption-${i}`} className="bg-white h-9 text-sm" />
        <button onClick={remove} data-testid={`site-photo-delete-${i}`} className="p-2 text-muted-foreground hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export default function AdminSite() {
  const [site, reloadSite] = useSite();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("caption", f.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " "));
        await api.post("/admin/photos", fd);
      }
      toast.success("Photos added to the site");
      reloadSite();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };

  if (!s || !site) return null;
  return (
    <div>
      <PageHeader eyebrow="Site, photos & alerts" title="Make it yours.">
        <Button onClick={() => ref.current.click()} disabled={busy} data-testid="photo-upload-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />}Upload photos
        </Button>
        <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.webp" multiple hidden data-testid="photo-upload-input" onChange={(e) => upload([...e.target.files])} />
      </PageHeader>
      <SiteContentEditor />
      <div className="grid xl:grid-cols-2 gap-6 mb-10">
        <FocusPanel s={s} setS={setS} photos={site.photos} />
        <AlertsPanel s={s} setS={setS} />
        <PasswordPanel />
      </div>
      <div className="eyebrow mb-4">Home page gallery · {site.photos.length} photos (captions save when you click away)</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {site.photos.map((p, i) => <PhotoCard key={p.id} p={p} i={i} onChanged={reloadSite} />)}
      </div>
    </div>
  );
}
